// Owner decision 2026-08-13: "We should always show to our users what they
// already paid for. They should also be allowed to track their top up
// credits." This is that surface.
//
// 🚨 NOT the Active Credits table, and deliberately separate from it. A pooled
// top-up never becomes a `workspace_credits` row — the pool does not enter the
// credits cascade at all — so it can never appear there. It lives in
// `workspace_text_topups` and is read through its own endpoint.
//
// 🚨 STRIPE-ONLY. Rendered from BillingPage (the Stripe branch) and never from
// ShopifyBillingView, which buys through Managed Pricing.
//
// ─────────────────────────────────────────────────────────────────────────
// 🚨 WHY THERE IS NO PER-ROW "REMAINING" COLUMN — read this before adding one.
//
// The payload DOES carry `tokens_remaining` and `tokens_consumed` on every
// purchase, and they are exact: consumption is FIFO by `purchased_at`
// ascending, verified in the backend implementation. So the usual objection —
// "the numbers would be made up" — is FALSE, and was the original (wrong)
// reason for leaving them out. Do not re-add the column on the strength of
// discovering they are accurate. They are. That was never the problem.
//
// The reason is that under FIFO a per-row remaining figure is a mechanical
// consequence of purchase ORDER, not information about the customer's money.
// The oldest pack is always the spent one, the newest is always untouched, and
// exactly one row is ever partially consumed. Someone with four purchases
// learns nothing from seeing which row the remainder sits in, because the
// answer is always "the oldest one that isn't empty yet". It also invites a
// support conversation whose only honest answer is "because that pack is
// older", which is not value delivered.
//
// The customer asks two questions and both are already answered: "how much do
// I have left" → the standing balance above the list; "what did I buy" → the
// ledger itself. The Active/Spent badge covers the one extra bit that belongs
// on a receipt: does this line still have value.
//
// So `tokens_remaining` / `tokens_consumed` are deliberately RETURNED by the
// backend and deliberately NOT RENDERED here — Storm documents them as
// support/reconciliation fields, which answer refund and dispute questions.
// That is a support workflow, not a customer-facing one. They are not an
// oversight to be "fixed" because they appear unused in the payload.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { Wallet, Info } from 'lucide-react';
import { billingAPI } from '../../services/api';
import { useBillingRefreshSource } from '../../contexts/BillingContext';
import { formatTokenCount } from './usageBars';
import type { TextTopUpPurchase } from '../../types/billing';

interface Props {
  workspaceId: string;
}

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

// 🚨 Money arrives in MAJOR units on this endpoint (`amount_paid_usd: 10.0` =
// ten dollars), NOT the minor units `billing_invoices` / the invoice ledger
// use. Reusing the ledger's formatter here renders $10.00 as $0.10 — hence a
// local formatter. `currency` is stamped per row from the real Stripe charge,
// so it is honoured rather than assuming a `$`.
const formatMoney = (n: number, currency?: string): string => {
  const code = (currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(n);
  } catch {
    // Unknown/garbled currency code — show the number with the code beside it
    // rather than silently mislabelling it as dollars.
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
  }
};

const TextTopUpHistory: React.FC<Props> = ({ workspaceId }) => {
  const [rows, setRows] = useState<TextTopUpPurchase[] | null>(null);
  const [balance, setBalance] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalPurchased, setTotalPurchased] = useState(0);
  const [totalCurrency, setTotalCurrency] = useState<string | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Page one, from scratch. Shared by the mount fetch and the Billing page's
  // Refresh button — a top-up bought in another tab (or a webhook that landed
  // after this page was opened) has to show up without a browser reload, and
  // this endpoint is a sibling of the billing payload, not part of it.
  const loadFirstPage = async (isAlive: () => boolean) => {
    try {
      const res = await billingAPI.listTextTopUps(workspaceId);
      if (!isAlive()) return;
      const d = res.data;
      setRows(d?.purchases ?? []);
      // 🚨 Every figure below is read from its own field, NEVER summed from
      // `purchases[]`. The backend aggregates over all rows; summing the
      // page is right only on page one of a short list and fails DOWNWARD,
      // understating money the customer still has.
      setBalance(d?.balance_tokens ?? 0);
      setTotalPaid(d?.total_paid_usd ?? 0);
      setTotalPurchased(d?.total_purchased_tokens ?? 0);
      setTotalCurrency(d?.currency);
      setTotalCount(d?.total_count ?? d?.purchases?.length ?? 0);
      setHasMore(d?.has_more === true);
      // A page-one reload also re-opens the section if it had been withheld
      // (flag was dark on mount and has since been turned on).
      setUnavailable(false);
    } catch {
      // 404 while the flag is dark is the EXPECTED state, not a fault, and
      // the customer did not initiate this fetch — so it fails silently and
      // the whole section is withheld rather than showing an error box on a
      // billing page. Same reasoning as `fetchCredits` in BillingPage.
      // 🚨 Only withhold when there is nothing on screen yet: a failed
      // refresh must never delete purchase history the customer is looking at.
      if (isAlive() && rows === null) setUnavailable(true);
    }
  };

  useEffect(() => {
    let alive = true;
    loadFirstPage(() => alive);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useBillingRefreshSource('text-topups', () => loadFirstPage(() => true));

  // Nothing expires any more, so this list only ever grows — a long-lived
  // workspace WILL pass the backend's 50-row page. Without this the older
  // purchases simply vanish with no indication, on the one surface whose
  // whole purpose is showing the customer everything they paid for.
  const loadMore = async () => {
    if (loadingMore || !rows) return;
    setLoadingMore(true);
    try {
      const res = await billingAPI.listTextTopUps(workspaceId, { offset: rows.length });
      const d = res.data;
      setRows((prev) => [...(prev ?? []), ...(d?.purchases ?? [])]);
      setHasMore(d?.has_more === true);
    } catch {
      // Leave what is already shown and stop offering more, rather than
      // replacing a good partial list with an error.
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // Nothing to say: flag dark, or a workspace that has never bought a top-up.
  // No empty state — an empty "purchase history" panel on a workspace that has
  // never purchased is noise, and worse, it can read as a purchase having gone
  // missing.
  if (unavailable || rows === null || rows.length === 0) return null;

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          AI Text Top-Ups
        </h2>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
        Everything you've purchased, and what's left. Top-up tokens never expire
        — each cycle your plan allowance is used first, and this balance is only
        drawn on once that runs out.
      </p>

      {/* Standing balance. 🚨 Rendered from `balance_tokens` and never summed
          from the rows below: the backend is the single source and two
          derivations of one balance is how a billing page ends up showing two
          different numbers. Same field the pool bar reads. */}
      <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 mb-5">
        <div className="flex items-start gap-3">
          <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
              Purchased tokens remaining
            </p>
            <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100 tabular-nums">
              {formatTokenCount(balance)}
            </p>
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5">
              {formatTokenCount(totalPurchased)} purchased in total ·{' '}
              {formatMoney(totalPaid, totalCurrency)} paid
            </p>
          </div>
        </div>
      </div>

      {/* Purchase ledger. Newest first, straight from the backend — no client
          re-sorting, so the order cannot drift from what the receipt emails
          say. */}
      {/* 🚨 THREE columns, and the Spent/Active badge sits UNDER the date
          rather than in a fourth column of its own. A four-column table needs
          ~420px, which at 375px pushes Status off-screen behind a horizontal
          scroll — and a distinction the customer can only reach by swiping is
          not a distinction. This is the same defect the invoice ledger hit.
          Three columns fit without any sideways scroll. */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <th className="py-2 pr-3">Purchased</th>
            <th className="py-2 px-3 text-right">Tokens</th>
            <th className="py-2 pl-3 text-right">Paid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            // 🚨 Fully-consumed purchases are RETURNED ON PURPOSE and must
            // render — a spent top-up is still a receipt, and hiding it would
            // erase the record of a payment, the opposite of what the owner
            // asked for. `tokens_remaining === 0` is "Spent", not "missing".
            const spent = p.tokens_remaining <= 0;
            return (
              <tr key={p.id} className="border-b border-gray-100 dark:border-gray-800 align-top">
                <td className="py-2.5 pr-3">
                  <span className="block text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {formatDate(p.purchased_at)}
                  </span>
                  <span
                    className={`mt-1 inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                      spent
                        ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    }`}
                  >
                    {spent ? 'Spent' : 'Active'}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right text-gray-900 dark:text-white font-semibold tabular-nums">
                  {formatTokenCount(p.tokens_granted)}
                </td>
                <td className="py-2.5 pl-3 text-right text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">
                  {formatMoney(p.amount_paid_usd, p.currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Paging. Full-width tap target at 375px. The count is shown alongside
          so the customer can tell the list is partial rather than assuming an
          old purchase has gone missing — the exact fear this whole section
          exists to answer. */}
      {hasMore && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-3 w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loadingMore
            ? 'Loading…'
            : `Show older purchases (${rows.length} of ${totalCount})`}
        </button>
      )}

      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          Top-ups have no expiry date — a purchase stays on your workspace until
          the tokens are used.
        </span>
      </p>
    </section>
  );
};

export default TextTopUpHistory;
